import { Link } from "react-router-dom";
import { MapPin, ArrowRight } from "lucide-react";

// A curated selection of major cities covered — actual coverage is nationwide.
const CITIES = [
  { name: "Orléans", top: "34%", left: "48%" },
  { name: "Paris", top: "22%", left: "50%" },
  { name: "Lille", top: "10%", left: "52%" },
  { name: "Strasbourg", top: "22%", left: "82%" },
  { name: "Nantes", top: "40%", left: "26%" },
  { name: "Lyon", top: "50%", left: "66%" },
  { name: "Bordeaux", top: "62%", left: "32%" },
  { name: "Toulouse", top: "76%", left: "44%" },
  { name: "Marseille", top: "78%", left: "68%" },
  { name: "Nice", top: "76%", left: "82%" },
];

/**
 * FranceCoverage — decorative "we're everywhere in France" section.
 * Uses a stylised SVG silhouette of France with gold accent points.
 */
export default function FranceCoverage() {
  return (
    <section
      data-testid="france-coverage-section"
      className="py-24 border-t border-white/5 bg-[#080808] relative overflow-hidden"
    >
      <div
        className="absolute inset-0 opacity-30 pointer-events-none"
        style={{
          background:
            "radial-gradient(ellipse at 50% 30%, rgba(212,175,55,0.08), transparent 60%)",
        }}
      />
      <div className="max-w-7xl mx-auto px-6 lg:px-10 relative grid lg:grid-cols-[1fr_1fr] gap-14 items-center">
        <div>
          <div className="text-xs tracking-[0.5em] gold uppercase">
            Zones de service
          </div>
          <h2 className="mt-3 font-serif text-white text-4xl sm:text-5xl leading-tight">
            Basé à <span className="gold">Orléans</span>,<br />
            disponible partout.
          </h2>
          <p className="mt-6 text-gray-400 max-w-lg leading-relaxed">
            Je travaille en solo, avec la rigueur et l'exigence d'un service
            sur-mesure. Basé à Orléans, j'interviens sur toute la France
            métropolitaine et me déplace en Europe pour les prestations qui
            le nécessitent — convoyage longue distance, VTC ou nettoyage
            haut de gamme, où que vous soyez.
          </p>

          <ul className="mt-8 grid grid-cols-2 gap-x-6 gap-y-2 text-sm text-gray-300">
            {CITIES.map((c) => (
              <li
                key={c.name}
                data-testid={`coverage-city-${c.name.toLowerCase()}`}
                className="flex items-center gap-2"
              >
                <MapPin size={12} className="gold" />
                {c.name}
              </li>
            ))}
          </ul>

          <div className="mt-4 text-[11px] uppercase tracking-widest gold">
            France · Europe sur demande
          </div>

          <div className="mt-10 flex flex-wrap gap-4">
            <Link
              to="/services/convoyage"
              data-testid="coverage-cta-convoyage"
              className="btn-gold inline-flex items-center gap-2"
            >
              Voir le Convoyage <ArrowRight size={16} />
            </Link>
            <Link
              to="/services/vtc"
              data-testid="coverage-cta-vtc"
              className="btn-outline-gold inline-flex items-center gap-2"
            >
              Voir le VTC <ArrowRight size={16} />
            </Link>
          </div>
        </div>

        {/* Stylised France map */}
        <div className="relative aspect-[4/5] max-w-md mx-auto lg:mx-0 lg:ml-auto w-full">
          <svg
            viewBox="0 0 100 120"
            className="absolute inset-0 w-full h-full"
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden="true"
          >
            <defs>
              <linearGradient id="franceFill" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#D4AF37" stopOpacity="0.18" />
                <stop offset="100%" stopColor="#D4AF37" stopOpacity="0.03" />
              </linearGradient>
              <linearGradient id="franceStroke" x1="0" y1="0" x2="1" y2="1">
                <stop offset="0%" stopColor="#D4AF37" stopOpacity="0.9" />
                <stop offset="100%" stopColor="#D4AF37" stopOpacity="0.35" />
              </linearGradient>
            </defs>
            {/* Simplified hexagonal France silhouette */}
            <path
              d="M40 6 L55 6 L62 10 L70 12 L78 18 L82 26 L86 34 L86 46 L82 54 L88 62 L84 72 L74 82 L64 92 L60 100 L52 104 L44 100 L36 96 L28 88 L22 78 L18 68 L14 58 L14 46 L18 38 L20 30 L26 22 L32 14 Z"
              fill="url(#franceFill)"
              stroke="url(#franceStroke)"
              strokeWidth="0.4"
            />
            {/* Corse */}
            <path
              d="M84 96 L90 96 L92 102 L90 108 L86 108 L84 104 Z"
              fill="url(#franceFill)"
              stroke="url(#franceStroke)"
              strokeWidth="0.4"
            />
          </svg>

          {/* City dots overlay */}
          {CITIES.map((c) => (
            <div
              key={c.name}
              className="absolute -translate-x-1/2 -translate-y-1/2 group"
              style={{ top: c.top, left: c.left }}
            >
              <div className="relative">
                <div className="w-2.5 h-2.5 rounded-full bg-[color:var(--gold)] shadow-[0_0_12px_rgba(212,175,55,0.8)]" />
                <div className="absolute inset-0 w-2.5 h-2.5 rounded-full bg-[color:var(--gold)] animate-ping opacity-60" />
              </div>
              <span className="absolute left-4 top-1/2 -translate-y-1/2 whitespace-nowrap text-[10px] tracking-widest uppercase text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity">
                {c.name}
              </span>
            </div>
          ))}

          <div className="absolute bottom-2 left-2 text-[10px] tracking-[0.3em] gold uppercase">
            10+ métropoles desservies
          </div>
        </div>
      </div>
    </section>
  );
}
